import React from 'react';
import {
  DocPage, PageHeader, Section,
  Para, Callout, Prerequisites, NextStep,
} from '../components/DocBlocks';
import { useIndustry } from '../IndustryContext';
import './ProductAttribute.css';

const TOC = [
  { id: 'overview',    label: 'Overview' },
  { id: 'vs-usage',    label: 'vs. Meters' },
  { id: 'example',     label: 'Example' },
  { id: 'definition',  label: 'Defining attributes' },
  { id: 'uses',        label: 'How they are used' },
];

// ── Sample usage table ────────────────────────────────────────────────────────

function SampleTable({ sampleTable }) {
  const { usageAttrs, productAttrs, rows } = sampleTable;
  return (
    <div className="pa-table-wrap">
      <table className="pa-table">
        <thead>
          <tr>
            <th className="pa-th">Usage Ref ID</th>
            {usageAttrs.map((h) => (
              <th key={h} className="pa-th">{h}</th>
            ))}
            {productAttrs.map((h) => (
              <th key={h} className="pa-th pa-th--product">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              <td className="pa-td pa-td--ref">{row.refId}</td>
              {row.usageVals.map((v, j) => (
                <td key={j} className="pa-td">{v}</td>
              ))}
              {row.productVals.map((v, j) => (
                <td key={j} className="pa-td pa-td--product">{v}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Attribute definition list ─────────────────────────────────────────────────

function AttrList({ attributes }) {
  return (
    <div className="pa-attr-list">
      {attributes.map((a) => (
        <div key={a.name} className="pa-attr-item">
          <span className="pa-attr-name">{a.name}</span>
          <span className="pa-attr-type">{a.type}</span>
          <div className="pa-attr-desc">
            {a.description}
            <span className="pa-attr-values">Values: {a.values}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function ProductAttribute({ onNavigate }) {
  const { industry } = useIndustry();
  const pa = industry.productAttribute;

  return (
    <DocPage toc={TOC}>
      <PageHeader
        eyebrow="Products"
        title="Product Attributes"
        description="A Product Attribute is metadata on a usage event — it travels alongside meter values for reporting and filtering, but does not affect the invoice."
      />

      <Prerequisites
        onNavigate={onNavigate}
        items={[
          { id: 'product',         label: 'Products' },
          { id: 'meter', label: 'Meters' },
        ]}
      />

      <Section id="overview" title="Overview">
        <Para>
          When recording usage, you send one or more meter values — the quantities
          the billing engine uses to compute charges. Product attributes travel alongside that
          same usage event as optional metadata. They do not affect the invoice amount, but
          they are stored with the usage record and can be queried later.
        </Para>
        <Para>
          For <em>{pa.product}</em>, {pa.description.toLowerCase()}
        </Para>
        <Callout type="info" title="Not used in pricing">
          Product attributes have no effect on how charges are calculated. They cannot be used
          in rate card lookups or as tier boundaries. Their purpose is to enrich the usage
          record with context that matters for operations and analytics — not billing math.
        </Callout>
      </Section>

      <Section id="vs-usage" title="Product attributes vs. Meters">
        <Para>
          Both are sent when recording usage for a product. The difference is in how the
          billing engine treats them:
        </Para>
        <div className="pa-compare">
          <div className="pa-compare-col pa-compare-col--usage">
            <div className="pa-compare-header">
              <span className="pa-compare-title">Meter</span>
              <span className="pa-compare-subtitle">The quantity that gets billed</span>
            </div>
            <div className="pa-compare-items">
              <div className="pa-compare-item">Numeric — a measured amount</div>
              <div className="pa-compare-item">Drives fee charges</div>
              <div className="pa-compare-item">Multipliers and mappings applied</div>
              <div className="pa-compare-item">Required for usage-based components</div>
            </div>
          </div>
          <div className="pa-compare-col pa-compare-col--product">
            <div className="pa-compare-header">
              <span className="pa-compare-title">Product Attribute</span>
              <span className="pa-compare-subtitle">Descriptive context about the event</span>
            </div>
            <div className="pa-compare-items">
              <div className="pa-compare-item">Any type — string, number, or enum</div>
              <div className="pa-compare-item">No effect on invoice amount</div>
              <div className="pa-compare-item">Stored with the usage record as-is</div>
              <div className="pa-compare-item">Optional — event is valid without them</div>
            </div>
          </div>
        </div>
      </Section>

      <Section id="example" title="Example">
        <Para>
          The table below shows how a single usage event carries both meters (the
          billable quantities, shown in plain columns) and a product attribute (shown in{' '}
          <span style={{ color: 'var(--purple-600, #9333ea)', fontWeight: 600 }}>purple</span>).
          The billing engine reads the meter values to compute charges and ignores the product attribute entirely.
        </Para>
        <SampleTable sampleTable={pa.sampleTable} />
      </Section>

      <Section id="definition" title="Defining attributes">
        <Para>
          Product attributes are defined at the product level. Each attribute has a name,
          a type, and an optional list of allowed values. Once defined, any usage event
          for that product can include values for those attributes.
        </Para>
        <AttrList attributes={pa.attributes} />
        <Callout type="info">
          Product attributes are optional on each usage event. If an event is sent without
          a value for a defined product attribute, the field is recorded as empty. The event
          is still processed and billed normally.
        </Callout>
      </Section>

      <Section id="uses" title="How they are used">
        <Para>
          Product attributes are available wherever usage records can be queried:
        </Para>
        <div className="pa-uses-list">
          {[
            { label: 'Analytics and reporting', desc: 'Filter or group usage by attribute value to break down revenue, volume, or trends — e.g. forward vs. return shipments, category-level transaction volume.' },
            { label: 'Operational dashboards',  desc: "Surface patterns in usage behaviour that don't affect billing but matter for operations." },
            { label: 'Audit trails',            desc: 'The full usage record, including product attributes, is retained for as long as the billing data is kept.' },
          ].map((item) => (
            <div key={item.label} className="pa-uses-item">
              <span className="pa-uses-label">{item.label}</span>
              <span className="pa-uses-desc">{item.desc}</span>
            </div>
          ))}
        </div>
      </Section>

      <NextStep
        onNavigate={onNavigate}
        items={[
          { id: 'usage', title: 'Recording Usage', desc: 'See how usage events are submitted and how product attributes travel alongside meter values.' },
        ]}
      />
    </DocPage>
  );
}
